import React, { useState, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { Park } from '../types';
import { PhotoUpload } from '../types/community';
import { communityService } from '../services/communityService';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import {
  Upload,
  X,
  Image as ImageIcon,
  Loader2,
  CheckCircle2,
  Share2,
} from 'lucide-react';
import { cn } from './ui/utils';
import { toast } from 'sonner';
import { useApp } from '../context/PetParksContext';

interface PhotoUploadModalProps {
  park: Park;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const PETSO_SHARE_FROM_PARK_KEY = 'petsoShareFromPark';

export const PhotoUploadModal: React.FC<PhotoUploadModalProps> = ({
  park,
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useApp();
  const navigate = useNavigate();
  const [photos, setPhotos] = useState<PhotoUpload[]>([]);
  const [captions, setCaptions] = useState<Record<string, string>>({});
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadDoneShowShare, setUploadDoneShowShare] = useState(false);
  const [lastUploadedPreviewUrl, setLastUploadedPreviewUrl] = useState<
    string | null
  >(null);

  const MAX_PHOTOS = 5;
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files) return;

      const validFiles: File[] = [];
      const errors: string[] = [];

      Array.from(files).forEach((file) => {
        // Check file type
        if (!ACCEPTED_TYPES.includes(file.type)) {
          errors.push(`${file.name}: ${t('photoInvalidType')}`);
          return;
        }

        // Check file size
        if (file.size > MAX_FILE_SIZE) {
          errors.push(`${file.name}: ${t('photoTooLarge')}`);
          return;
        }

        // Check total count
        if (photos.length + validFiles.length >= MAX_PHOTOS) {
          errors.push(t('photoMaxAllowed', { count: String(MAX_PHOTOS) }));
          return;
        }

        validFiles.push(file);
      });

      // Show errors
      if (errors.length > 0) {
        toast.error(errors[0]);
      }

      // Add valid files
      if (validFiles.length > 0) {
        const newPhotos: PhotoUpload[] = validFiles.map((file) => ({
          id: `temp-${Date.now()}-${Math.random()}`,
          park_id: park.id,
          user_id: 'current-user-id', // TODO: Get from auth
          file,
          preview_url: URL.createObjectURL(file),
          status: 'pending',
        }));

        setPhotos((prev) => [...prev, ...newPhotos]);
      }
    },
    [photos.length, park.id]
  );

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const removePhoto = (photoId: string) => {
    setPhotos((prev) => {
      const photo = prev.find((p) => p.id === photoId);
      if (photo) {
        URL.revokeObjectURL(photo.preview_url);
      }
      return prev.filter((p) => p.id !== photoId);
    });
    setCaptions((prev) => {
      const next = { ...prev };
      delete next[photoId];
      return next;
    });
  };

  const handleSubmit = async () => {
    if (photos.length === 0) {
      toast.error(t('photoSelectAtLeastOne'));
      return;
    }

    setIsUploading(true);
    setLastUploadedPreviewUrl(photos[0]?.preview_url ?? null);

    try {
      // Upload photos sequentially
      for (const photo of photos) {
        const photoWithCaption = {
          ...photo,
          caption: captions[photo.id],
        };

        await communityService.uploadPhoto(photoWithCaption);
      }

      toast.success(
        t('photoUploadedPendingModeration', {
          count: String(photos.length),
          suffix: photos.length > 1 ? 's' : '',
        })
      );
      setUploadDoneShowShare(true);
      onSuccess?.();
    } catch (error) {
      toast.error(t('photoUploadFailed'));
      console.error('Photo upload error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleShareToSocial = () => {
    try {
      sessionStorage.setItem(
        PETSO_SHARE_FROM_PARK_KEY,
        JSON.stringify({
          parkName: park.name,
          parkId: park.id,
          imageUrl: lastUploadedPreviewUrl ?? undefined,
        })
      );
    } catch {
      // ignore
    }
    handleClose();
    navigate({
      to: '/dashboard/social',
      search: { create: true, park: park.name },
    });
  };

  const handleClose = () => {
    if (!uploadDoneShowShare) {
      photos.forEach((photo) => URL.revokeObjectURL(photo.preview_url));
    }
    setPhotos([]);
    setCaptions({});
    setUploadDoneShowShare(false);
    setLastUploadedPreviewUrl(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {uploadDoneShowShare ? t('photosUploaded') : t('uploadPhotos')}
          </DialogTitle>
          <DialogDescription>
            {uploadDoneShowShare
              ? t('photosUploadedDescription', { parkName: park.name })
              : t('photosUploadDescription', { parkName: park.name })}
          </DialogDescription>
        </DialogHeader>

        {uploadDoneShowShare ? (
          <div className="space-y-4 py-4">
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 text-center">
              <CheckCircle2 className="mx-auto mb-2 h-12 w-12 text-primary" />
              <p className="font-medium">{t('alsoShareToSocial')}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('alsoShareToSocialDescription', { parkName: park.name })}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1 cursor-pointer"
              >
                {t('done')}
              </Button>
              <Button
                type="button"
                onClick={handleShareToSocial}
                className="flex-1 cursor-pointer"
              >
                <Share2 className="mr-2 h-4 w-4" />
                {t('shareToSocial')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Upload Area */}
            {photos.length < MAX_PHOTOS && (
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={cn(
                  'relative cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors',
                  dragActive
                    ? 'border-primary bg-primary/5'
                    : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50'
                )}
              >
                <input
                  type="file"
                  multiple
                  accept={ACCEPTED_TYPES.join(',')}
                  onChange={(e) => handleFiles(e.target.files)}
                  className="absolute inset-0 cursor-pointer opacity-0"
                  disabled={isUploading}
                />
                <div className="flex flex-col items-center gap-2">
                  <div className="rounded-full bg-primary/10 p-3">
                    <Upload className="h-8 w-8 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{t('photoDropOrBrowse')}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t('photoFormatsHint', { count: String(MAX_PHOTOS) })}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Photo Previews */}
            {photos.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>
                    {t('photosCount', {
                      current: String(photos.length),
                      max: String(MAX_PHOTOS),
                    })}
                  </Label>
                  {photos.length >= MAX_PHOTOS && (
                    <Badge variant="secondary" className="text-xs">
                      {t('maximumReached')}
                    </Badge>
                  )}
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  {photos.map((photo) => (
                    <Card key={photo.id} className="relative overflow-hidden">
                      <div className="aspect-video w-full overflow-hidden bg-muted">
                        <img
                          src={photo.preview_url}
                          alt={t('photoPreviewAlt')}
                          className="h-full w-full object-cover"
                        />
                      </div>

                      {/* Remove Button */}
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute right-2 top-2 h-8 w-8 cursor-pointer"
                        onClick={() => removePhoto(photo.id)}
                        disabled={isUploading}
                      >
                        <X className="h-4 w-4" />
                      </Button>

                      {/* Caption */}
                      <div className="p-3">
                        <Input
                          placeholder={t('photoCaptionOptional')}
                          value={captions[photo.id] || ''}
                          onChange={(e) =>
                            setCaptions((prev) => ({
                              ...prev,
                              [photo.id]: e.target.value,
                            }))
                          }
                          maxLength={200}
                          disabled={isUploading}
                          className="text-sm"
                        />
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Info Message */}
            <div className="rounded-md bg-blue-50 p-3 dark:bg-blue-900/20">
              <div className="flex gap-2">
                <ImageIcon className="h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <p className="font-medium">{t('moderationNotice')}</p>
                  <p className="mt-1 text-xs">
                    {t('moderationNoticeDescription')}
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1 cursor-pointer"
                disabled={isUploading}
              >
                {t('cancel')}
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                className="flex-1 cursor-pointer"
                disabled={photos.length === 0 || isUploading}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('uploading')}
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    {t('uploadAction', {
                      count:
                        photos.length > 0 ? ` (${String(photos.length)})` : '',
                    })}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
